import React, { useEffect, useRef, useState } from 'react';
import { Check, Trash2, ExternalLink, Newspaper, Megaphone, Cog, Bell, BarChart3, ShieldAlert, ChevronRight, Maximize2, Zap } from 'lucide-react';
import { NotificationItem, NotificationCategory, resolvePresentation, isBreakingNews, BREAKING_NEWS_GRADIENT, BREAKING_NEWS_INK } from '../../composables/notificationAPI';
import NotificationDetailModal from './NotificationDetailModal';

const CATEGORY_ICONS: Partial<Record<NotificationCategory, React.ComponentType<{ size?: number }>>> = {
  ARTICLE: Newspaper,
  ANNOUNCEMENT: Megaphone,
  PORTFOLIO: BarChart3,
  SYSTEM: Cog,
  SECURITY: ShieldAlert,
};

function timeAgo(iso?: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const diffMin = Math.round((Date.now() - date.getTime()) / 60000);
  if (diffMin < 1) return 'เมื่อสักครู่';
  if (diffMin < 60) return `${diffMin} นาทีที่แล้ว`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr} ชม.ที่แล้ว`;
  return date.toLocaleDateString('th-TH', { day: 'numeric', month: 'short' });
}

const pillBtn = 'inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-full border transition-colors duration-150 active:scale-95';

const NotificationRow: React.FC<{
  item: NotificationItem;
  onToggleRead: () => void;
  onAcknowledge: () => void;
  onDismiss: () => void;
  onOpen: (url: string) => void;
}> = ({ item, onToggleRead, onAcknowledge, onDismiss, onOpen }) => {
  const unread = !item.state?.readAt;
  const primaryUrl = item.deepLink;
  const breaking = isBreakingNews(item.type);
  const CategoryIcon = breaking ? Zap : CATEGORY_ICONS[item.category] ?? Bell;
  // ให้ backend เป็นคนบอก label/icon/cta จริง (มากับ field presentation/primaryActionLabel ของ item
  // จาก feed แล้ว) — เดาเองในนี้ใช้เป็นแค่ fallback ตอน backend ยังไม่ส่งมา
  const presentation = resolvePresentation(item);

  // เช็คว่า title (truncate บรรทัดเดียว) หรือ body (line-clamp 2 บรรทัด) ถูกตัดจริงมั้ย
  // (วัดจาก DOM ตรง ๆ แทนการเดาความยาว string เพราะความกว้างจอ/ฟอนต์แต่ละคนไม่เท่ากัน)
  // ถ้าถูกตัดถึงโชว์ปุ่ม "ดูรายละเอียด" ให้กดเปิด popup อ่านเต็ม ๆ
  const titleRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLParagraphElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    const check = () => {
      const titleCut = !!titleRef.current && titleRef.current.scrollWidth > titleRef.current.clientWidth;
      const bodyCut = !!bodyRef.current && bodyRef.current.scrollHeight > bodyRef.current.clientHeight;
      setIsTruncated(titleCut || bodyCut);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [item.title, item.body]);

  const handleRowClick = () => {
    if (primaryUrl) { onOpen(primaryUrl); return; }
    // ไม่มีลิงก์ให้เปิด แต่การกดเข้ามาดูก็ถือว่า "อ่านแล้ว" — ไม่ toggle กลับถ้าอ่านแล้ว
    if (unread) onToggleRead();
  };
  return (
    <div
      className="relative px-4 py-3 border-b cursor-pointer group transition-colors duration-300"
      style={{
        borderColor: 'var(--bg-item)',
        background: breaking && unread
          ? 'linear-gradient(90deg, rgba(34,197,94,0.14), rgba(34,197,94,0.03) 65%, transparent)'
          : unread ? 'var(--bg-item)' : 'transparent',
      }}
      onClick={handleRowClick}
      title={primaryUrl ? 'คลิกเพื่อเปิด' : undefined}
    >
      {breaking && <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: BREAKING_NEWS_GRADIENT }} />}
      <div className="flex items-start gap-3">
        <div
          className="w-12 h-12 rounded-lg overflow-hidden shrink-0"
          style={{ background: breaking ? BREAKING_NEWS_GRADIENT : 'var(--bg-navbar, #e5e7eb)' }}
        >
          {item.image ? (
            <img src={item.image} alt="" className="w-full h-full object-cover" loading="lazy" />
          ) : presentation.icon ? (
            // icon จาก backend เป็น emoji เดี่ยว ๆ (เช่น "📊") — render เป็น text ตรง ๆ
            <div className="w-full h-full flex items-center justify-center text-xl">{presentation.icon}</div>
          ) : (
            <div className="w-full h-full flex items-center justify-center" style={{ color: breaking ? BREAKING_NEWS_INK : 'var(--secondary-text-color)' }}>
              <CategoryIcon size={18} {...(breaking ? { strokeWidth: 2.5 } : {})} />
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2 mb-1">
            {breaking ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-extrabold px-2 py-0.5 rounded-full" style={{ background: BREAKING_NEWS_GRADIENT, color: BREAKING_NEWS_INK }}>
                <Zap size={10} /> ข่าวด่วน
              </span>
            ) : (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--bg-navbar, #e5e7eb)', color: 'var(--secondary-text-color)' }}>
                {presentation.icon ? `${presentation.icon} ${presentation.label}` : presentation.label}
              </span>
            )}
            <span className="text-[10px] opacity-60 whitespace-nowrap">{timeAgo(item.publishedAt)}</span>
          </div>
          <div ref={titleRef} className={`text-sm truncate ${unread ? 'font-bold' : 'font-medium'}`}>{item.title}</div>
          <p ref={bodyRef} className="text-xs opacity-75 mt-0.5 line-clamp-2 whitespace-pre-wrap">{item.body}</p>
          {isTruncated && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setShowDetail(true); }}
              className="inline-flex items-center gap-1 text-[11px] font-semibold mt-1 opacity-90 hover:opacity-100 transition-opacity"
              style={{ color: 'var(--yellow-text-color)' }}
            >
              <Maximize2 size={10} /> ดูรายละเอียด
            </button>
          )}
        </div>
      </div>

      {(item.actions?.length ?? 0) > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {item.actions!.map((a, i) => (
            <button
              key={i}
              onClick={(e) => { e.stopPropagation(); onOpen(a.url); }}
              className="text-[11px] font-semibold px-2 py-1 rounded-lg flex items-center gap-1 transition-colors duration-150 active:scale-95"
              style={{ backgroundColor: 'var(--button-bg-color)', color: 'var(--button-text-color)' }}
            >
              {a.label} <ExternalLink size={11} />
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 mt-2.5" onClick={(e) => e.stopPropagation()}>
        {/* แค่บอกสถานะ ไม่ใช่ปุ่มกดสลับแล้ว — คลิกที่ตัวรายการ (การ์ดเอง) จะถือว่าอ่านแล้วแทน (ดู
            handleRowClick ด้านบน) เอาปุ่ม toggle read/unread ในนี้ออกเพราะเดิม label ขึ้นเป็นคำตรงข้าม
            สถานะจริงเสมอ (บอกสิ่งที่จะเกิดขึ้นถ้ากด ไม่ใช่บอกสถานะปัจจุบัน) ทำให้สับสน */}
        {unread ? (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5" style={{ color: 'var(--secondary-text-color)' }}>
            ยังไม่อ่าน
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5" style={{ color: 'var(--green-text-color)' }}>
            <Check size={12} /> อ่านแล้ว
          </span>
        )}
        {/* CTA แสดงเฉพาะเมื่อมี deepLink จริง — ไม่มีลิงก์ไม่สร้างปุ่มเดาปลายทาง */}
        {primaryUrl && !(item.actions?.length) && (
          <button
            onClick={() => onOpen(primaryUrl)}
            className={pillBtn}
            style={{ borderColor: 'var(--bg-item)', color: 'var(--text-color)', backgroundColor: 'transparent' }}
          >
            {presentation.cta} <ChevronRight size={12} />
          </button>
        )}
        {item.acknowledgement?.required && !item.state?.acknowledgedAt && (
          <button
            onClick={onAcknowledge}
            className={pillBtn}
            style={{ borderColor: 'var(--green-text-color)', backgroundColor: 'var(--green-text-color)', color: '#fff' }}
          >
            {item.acknowledgement.label || 'รับทราบ'}
          </button>
        )}
        {item.acknowledgement?.required && item.state?.acknowledgedAt && (
          <span className="text-[11px] font-semibold" style={{ color: 'var(--green-text-color)' }}>✓ รับทราบแล้ว</span>
        )}
        <button
          onClick={onDismiss}
          className={pillBtn + ' ml-auto'}
          style={{ borderColor: 'var(--bg-item)', color: 'var(--secondary-text-color)', backgroundColor: 'transparent' }}
        >
          <Trash2 size={12} /> ซ่อน
        </button>
      </div>

      {showDetail && (
        <NotificationDetailModal
          item={item}
          onClose={() => setShowDetail(false)}
          onOpen={(url) => { setShowDetail(false); onOpen(url); }}
        />
      )}
    </div>
  );
};

export default NotificationRow;
